import { z } from 'zod';
import type { SettingToken } from '@sleed/bizcommerce-api';
import { isValidLoyaltySettingValue, loyaltySettingKind } from '~/lib/loyalty-settings';

/**
 * Setting edit form. Mirrors the `SettingToken` DTO and the legacy admin grid validation
 * (Core/client/admin/src/pages/System/Settings/index.js): pn required ≤80, pv required with no length cap
 * (Settings.ParamValue is nvarchar(max) since BW-8341: the Apple Pay merchant identity certificate is a setting).
 * `wsid` is the site override (null = base setting, per the Settings AGENTS.md). `prmg`/`descr`
 * are carried through so an update never wipes them (legacy onDataGridRowUpdatingMergeUnchanged).
 */
export const settingSchema = z
	.object({
		id: z.number().optional(),
		pn: z.string().trim().min(1, 'validation.required').max(80, 'validation.tooLong'),
		pv: z.string().trim().min(1, 'validation.required'),
		wsid: z.number().nullable(),
		prmg: z.string().nullable(),
		descr: z.string().nullable()
	})
	// A loyalty setting takes only a value its getter reads (the server refuses the rest too).
	.superRefine((form, ctx) => {
		if (!isValidLoyaltySettingValue(form.pn, form.pv))
			ctx.addIssue({ code: 'custom', path: ['pv'], message: `loyalty.overview.valueHint.${loyaltySettingKind(form.pn)}` });
	});

export type SettingFormValues = z.infer<typeof settingSchema>;

export const EMPTY_SETTING: SettingFormValues = { pn: '', pv: '', wsid: null, prmg: null, descr: null };

export function tokenToForm(token: SettingToken): SettingFormValues {
	return {
		id: token.id,
		pn: token.pn ?? '',
		pv: token.pv ?? '',
		wsid: token.wsid ?? null,
		prmg: token.prmg ?? null,
		descr: token.descr ?? null
	};
}

export function formToToken(form: SettingFormValues): SettingToken {
	return {
		id: form.id,
		pn: form.pn.trim(),
		pv: form.pv.trim(),
		wsid: form.wsid,
		prmg: form.prmg,
		descr: form.descr
	};
}
